import { AxiosPromise } from "axios";
import { ValidationResult } from "formvalidation/hooks";
import React, { createContext, useEffect, useState } from "react";
import { InPageEditableSectionField } from "./types";
import equal from "fast-deep-equal";

export interface InPageEditingState {
  fieldKey: string | undefined;
  value: any | undefined;
  initialValue: any | undefined;
  isUpdating: boolean;
  error: string | undefined;
  submitFunc: (value: any) => AxiosPromise<server.dto.ApiResponseWrapper<any> | server.dto.ApiResponse>;
}

const defaultInPageEditingState: InPageEditingState = {
  fieldKey: undefined,
  value: undefined,
  initialValue: undefined,
  isUpdating: false,
  error: undefined,
  submitFunc: () => undefined,
}

export interface InPageEditingContextValue {
  enabled: boolean;
  editingField: InPageEditingState;
  startInPageEdit: (field: InPageEditableSectionField) => void;
  updateValue: (value: any) => void;
  saveInPageEdit: (value: any) => void;
  clearInPageEdit: () => void;
}

const defaultInPageEditingContext = {
  enabled: false,
  editingField: defaultInPageEditingState,
  startInPageEdit: () => { },
  updateValue: () => { },
  saveInPageEdit: () => { },
  clearInPageEdit: () => { },
}

export const InPageEditingContext = createContext<InPageEditingContextValue>(defaultInPageEditingContext);

const useInPageEditingState = () => {
  const [editingField, setEditingField] = useState<InPageEditingState>(defaultInPageEditingState);
  const [nextField, setNextField] = useState<InPageEditingState | undefined>(undefined);

  useEffect(() => {
    if (editingField.fieldKey === undefined && nextField !== undefined) {
      setEditingField(nextField);
      setNextField(undefined);
    }
  }, [editingField, nextField]);

  return [editingField, setEditingField, setNextField] as const;
}

interface InPageEditingProviderProps {
  enabled: boolean;
  validate: () => Promise<ValidationResult>;
  onValueChanged: (key: string, value: any) => (Promise<void> | void);
  onSuccessfulUpdate?: (key: string, value: any) => Promise<void>;
}

export const InPageEditingProvider = (props: React.PropsWithChildren<InPageEditingProviderProps>) => {
  const [editingField, setEditingField, setNextField] = useInPageEditingState();

  const startInPageEdit = (field: InPageEditableSectionField) => {
    setNextField(state => ({
      ...state,
      fieldKey: field.key,
      value: field.inPageEdit!.initialValue,
      initialValue: field.inPageEdit!.initialValue,
      submitFunc: field.inPageEdit!.submitChange,
    }));
  }
  const updateValue = (value: any) => setEditingField(state => ({ ...state, value }));
  const clearInPageEdit = () => setEditingField(defaultInPageEditingState);

  const saveInPageEdit = async (value: any) => {
    if (equal(value, editingField.initialValue)) {
      clearInPageEdit();
      return;
    }

    if (editingField.isUpdating) return;

    const validatorResult = await props.validate();
    if (validatorResult.hasErrors) {
      return;
    }

    setEditingField(state => ({ ...state, isUpdating: true }));
    await props.onValueChanged(editingField.fieldKey, value);

    try {
      const response = await editingField.submitFunc(value);
      if (response.data.hasErrors) {

        setEditingField(state => ({
          ...state,
          value: state.initialValue,
          isUpdating: false,
          error: response.data.errors[0].message
        }));
        await props.onValueChanged(editingField.fieldKey, editingField.initialValue);

        return;
      } else {
        if (props.onSuccessfulUpdate) {
          await props.onSuccessfulUpdate(editingField.fieldKey, editingField.initialValue);
        }
      }
    } catch (e: any) {
      console.error(e);
      setEditingField(state => ({
        ...state,
        value: state.initialValue,
        isUpdating: false,
        error: RESX.HttpError.Error500Title
      }));
      await props.onValueChanged(editingField.fieldKey, editingField.initialValue);
      return;
    }
    clearInPageEdit();
  }

  return <InPageEditingContext.Provider value={{
    enabled: props.enabled,
    editingField,
    startInPageEdit,
    updateValue,
    saveInPageEdit,
    clearInPageEdit
  }}>
    {props.children}
  </InPageEditingContext.Provider>
}